import {
  QueryFactory,
  SubtenantDto,
  MovePatientDto,
  AppointmentStatusCodes,
} from '@lmt/shared-types';

import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { getFullName } from '@lmt/unicorn-shared/app/components/patients/patient-form/common/common';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { getDefaultTabs } from '@lmt/unicorn-shared/app/features/navigation/selectors';
import {
  confirmAnonymizePatient,
  confirmArchivePatient,
  confirmMovePatient,
  confirmMovePatientWithNumberReplace,
  confirmPermanentlyDeletePatient,
  confirmRestorePatient,
} from '@lmt/unicorn-shared/app/features/patients/confirmations';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  TableDotMenu,
  MenuDirection,
} from '../../../common/table/TableDotMenu';
import localStyles from './EditPatientSubmenu.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useEventCallback } from '@mui/material';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { findKey } from 'lodash';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';

type Props = {
  patientId: number;
} & Pick<
  FormComponentProps,
  'formId' | 'loadBlocking' | 'formType' | 'tryCompleteForm'
>;

export const EditPatientSubmenu: FC<Props> = React.memo(
  function EditPatientSubmenu(props) {
    const { formType, formId, tryCompleteForm, patientId, loadBlocking } =
      props;

    const patientsTab = useUniSelector(getDefaultTabs).find(
      (x) => x.id == 'patients',
    )!;
    const i18next = useScopedTranslation('patients.form.edit_patient');
    const claims = useClaims();
    const queryClient = useQueryClient();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const navigate = useNavigate();
    const forms = useGlobalForms();
    const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
      props.patientId,
    );
    const subtenants = useSubtenantsDict();

    const onDelete = useEventCallback(async () => {
      const reason = await confirmArchivePatient(modals);
      if (reason) {
        await removePatient(reason);
      }
    });

    const removePatient = useEventCallback(async (reason: string) => {
      loadBlocking(formId, i18next.t('delete_loading'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.PatientClient.removePatient(patientId, {
              reason,
            });
            toast(i18next.t('toasts.archive_successful'), {
              type: 'success',
            });
            await cancelVisits(patientId);
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch((e) => console.error('removePatient', e));
    });

    const onPermanentlyDelete = useEventCallback(async () => {
      if (!patientQuery.data) return;
      const isConfirmed = await confirmPermanentlyDeletePatient(
        modals,
        getFullName(patientQuery.data),
      );
      if (isConfirmed) {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.PatientClient.deletePermanently(patientId);
            dispatch(NavigationActions.patientRemoved(patientId));
            // do not invalidate queries, since it will cause 404 errors
            //await invalidateQueries();
            navigate({
              pathname: patientsTab?.path,
              search: patientsTab?.search,
            });
            toast(i18next.t('toasts.delete_successful'), {
              type: 'success',
            });
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }
    });

    const restorePatient = useEventCallback(async () => {
      const isConfirmed = await confirmRestorePatient(modals);
      if (isConfirmed) {
        loadBlocking(formId, i18next.t('delete_loading'), async () => {
          try {
            await tryCompleteForm(formType, formId, async () => {
              await QueryFactory.PatientClient.restorePatient(patientId);
            });
          } catch (err) {
            await modals.showError({ text: convertToErrorString(err) });
          }
        }).catch((e) => console.error('restorePatient', e));
      }
    });

    const onAnonymize = useEventCallback(async () => {
      if (!patientQuery.data) return;

      const reason = await confirmAnonymizePatient(
        modals,
        getFullName(patientQuery.data),
      );
      if (reason) {
        try {
          await tryCompleteForm(formType, formId, async () => {
            const removeDto = { reason };
            await QueryFactory.PatientClient.anonymize(patientId, removeDto);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey(patientId),
            });
            toast(i18next.t('toasts.anonymize_successful'), {
              type: 'success',
            });
            await cancelVisits(patientId);
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }
    });

    const movePatient = useEventCallback(async () => {
      if (!patientQuery.data) return;
      const subtenantName = await confirmMovePatient(
        modals,
        claims.subtenants
          .filter((s) => s !== patientQuery.data.subtenantId)
          .map((s) => subtenants[s].name),
      );
      if (!subtenantName) return;
      const subtenantId = findKey(subtenants, { name: subtenantName });
      if (!subtenantId) return;

      try {
        await tryCompleteForm(formType, formId, async () => {
          await QueryFactory.PatientClient.movePatient(
            patientId,
            parseInt(subtenantId),
            {
              isReplaceNumberWithGeneratedIfDuplicate: false,
            },
          );
          toast(i18next.t('toasts.move_successful'), {
            type: 'success',
          });
        });
      } catch (err) {
        const errorText = convertToErrorString(err);
        if (errorText.startsWith('number:')) {
          const isConfirmed = await confirmMovePatientWithNumberReplace(modals);
          if (isConfirmed) {
            await movePatientWithNumberReplace(parseInt(subtenantId));
          }
        } else {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }
    });

    const movePatientWithNumberReplace = useEventCallback(
      async (subtenantId: number) => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            const result = await QueryFactory.PatientClient.movePatient(
              patientId,
              subtenantId,
              {
                isReplaceNumberWithGeneratedIfDuplicate: true,
              },
            );
            toast(i18next.t('toasts.move_successful'), {
              type: 'success',
            });
            if (!result.duplicatePatientId) {
              return;
            }
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.PatientQuery.getPatientQueryKey(patientId),
            });
            await forms.openMergePair(patientId, result.duplicatePatientId);
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      },
    );

    const cancelVisits = useEventCallback(async (patientId: number) => {
      const now = new Date();
      const visits =
        (
          await QueryFactory.AppointmentEventsClient.searchVisits(
            patientId,
            null,
            null,
            null,
            null,
            now,
          )
        )?.filter(
          (visit) => visit.statusId !== AppointmentStatusCodes.Canceled,
        ) ?? [];
      if (visits.length > 0) {
        try {
          for (const visit of visits) {
            await QueryFactory.AppointmentEventsClient.cancelVisit(visit.id);
          }
          toast(i18next.t('toasts.visit_canceled_successful'), {
            type: 'success',
          });
        } catch (error) {
          await modals.showError({ text: convertToErrorString(error) });
        }
      }
    });

    return (
      <div>
        <TableDotMenu
          className={localStyles.container}
          direction={MenuDirection.TopRight}
          testId="edit-patient-submenu"
          options={[
            {
              title: i18next.t('options.move_patient'),
              onClick: movePatient,
              testId: 'editPatient-moveButton',
              isHidden:
                claims.subtenants.length === 1 ||
                !claims.permissions.movePatientsToAnotherSubtenant,
            },
            {
              title: i18next.t('options.restore_patient'),
              onClick: restorePatient,
              testId: 'editPatient-restoreButton',
              isHidden:
                !patientQuery.data?.isDeleted ||
                patientQuery.data?.anonymous ||
                !claims.permissions.patientDeleteAndRestore,
            },
            {
              title: i18next.t('options.archive'),
              testId: 'editPatient-deleteButton',
              onClick: onDelete,
              isDeleteItem: true,
              isUnderLine: true,
              isHidden:
                patientQuery.data?.isDeleted ||
                patientQuery.data?.anonymous ||
                !claims.permissions.patientDeleteAndRestore,
            },
            {
              title: i18next.t('options.permanently_delete'),
              onClick: onPermanentlyDelete,
              isDeleteItem: true,
              isUnderLine: true,
              isHidden:
                !claims.permissions.patientAnonymizeAndDeletePermanently,
            },
            {
              title: i18next.t('options.anonymize'),
              onClick: onAnonymize,
              isDeleteItem: true,
              isUnderLine: true,
              isHidden:
                patientQuery.data?.anonymous ||
                !claims.permissions.patientAnonymizeAndDeletePermanently,
            },
          ]}
        />
      </div>
    );
  },
);
